import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { fmtINR, monthName } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Printer, Download } from 'lucide-react';

export default function PayslipPrintPage() {
  const { runId, employeeId } = useParams();
  const [run, setRun] = useState(null);
  const [entry, setEntry] = useState(null);
  const [company, setCompany] = useState(null);
  const [emp, setEmp] = useState(null);
  const [tpl, setTpl] = useState(null);

  useEffect(() => {
    (async () => {
      const r = await api.get(`/payroll/runs/${runId}`);
      setRun(r.data);
      const e = r.data.entries.find((x) => x.employee_id === employeeId);
      setEntry(e);
      const c = await api.get(`/companies/${r.data.company_id}`);
      setCompany(c.data);
      const empRes = await api.get(`/payroll/employees?company_id=${r.data.company_id}`);
      setEmp((empRes.data || []).find((x) => x.id === employeeId));
      try {
        const t = await api.get('/admin/payslip-template');
        setTpl(t.data);
      } catch { setTpl({}); }
    })();
  }, [runId, employeeId]);

  if (!run || !entry) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const br = entry.breakdown;
  const showBank = tpl?.show_bank_details !== false;
  const showPan = tpl?.show_pan !== false;
  const showEmpContrib = tpl?.show_employer_contrib !== false;
  const accent = tpl?.accent_color || '#0f766e';
  const disclaimer = tpl?.disclaimer || 'This is a system-generated payslip.';
  const signature = tpl?.signature_label || 'Authorised signatory';
  const totalDeductions = Number(br.deductions.total || 0)
    + Number(entry.extra_deduction || 0)
    + Number(entry.loan_deduction || 0);
  const grossEarnings = Number(br.gross)
    + Number(entry.overtime_amount || 0)
    + Number(entry.extra_allowance || 0)
    - Number(entry.lop_deduction || 0);
  const finalNet = entry.net_pay ?? (grossEarnings - totalDeductions);

  return (
    <div className="min-h-screen bg-white p-6 md:p-12 print-page">
      <div className="no-print mb-4 flex justify-between items-center max-w-3xl mx-auto">
        <a href={-1} onClick={(e) => { e.preventDefault(); window.close(); }} className="text-sm text-primary hover:underline">← Close</a>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} data-testid="print-payslip"><Printer className="h-4 w-4 mr-2" />Print</Button>
          <Button onClick={() => window.print()} data-testid="download-payslip-pdf"><Download className="h-4 w-4 mr-2" />Download PDF</Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto">
        {tpl?.header_html && (
          <div className="mb-4 text-sm" dangerouslySetInnerHTML={{ __html: tpl.header_html }} />
        )}
        <div className="flex items-start justify-between border-b pb-4" style={{ borderColor: accent }}>
          <div className="flex items-start gap-3">
            {tpl?.company_logo_url && (
              <img src={tpl.company_logo_url} alt="logo" style={{ height: 44 }} className="rounded object-contain" />
            )}
            <div>
              <h1 className="font-display text-2xl font-semibold" style={{ color: accent }}>{company?.name}</h1>
              <p className="text-sm text-muted-foreground">{company?.address || 'India'}</p>
              <p className="text-sm text-muted-foreground">GST: {company?.gst || '—'} · PAN: {company?.pan || '—'}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-display font-semibold">Payslip</p>
            <p className="text-sm">{monthName(run.month)} {run.year}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
          <div><p className="text-muted-foreground text-xs">Employee</p><p className="font-medium">{entry.name}</p></div>
          <div><p className="text-muted-foreground text-xs">Employee code</p><p>{entry.employee_code || '—'}</p></div>
          <div><p className="text-muted-foreground text-xs">Role</p><p>{emp?.role || '—'}</p></div>
          {showPan && <div><p className="text-muted-foreground text-xs">PAN</p><p>{emp?.pan || '—'}</p></div>}
          {showBank && <div><p className="text-muted-foreground text-xs">Bank</p><p>{emp?.bank_name || '—'}</p></div>}
          {showBank && <div><p className="text-muted-foreground text-xs">Account · IFSC</p><p>{emp?.bank_account || '—'} · {emp?.bank_ifsc || '—'}</p></div>}
        </div>

        <div className="grid grid-cols-2 gap-6 mt-6">
          <div>
            <h3 className="font-display font-semibold mb-2 pb-1 border-b" style={{ borderColor: accent }}>Earnings</h3>
            <div className="space-y-1 text-sm tabular-nums">
              <div className="flex justify-between"><span>Basic</span><span>{fmtINR(br.earnings.basic)}</span></div>
              <div className="flex justify-between"><span>HRA</span><span>{fmtINR(br.earnings.hra)}</span></div>
              <div className="flex justify-between"><span>Special allowance</span><span>{fmtINR(br.earnings.special_allowance)}</span></div>
              <div className="flex justify-between"><span>Other allowance</span><span>{fmtINR(br.earnings.other_allowance)}</span></div>
              <div className="flex justify-between"><span>Bonus</span><span>{fmtINR(br.earnings.bonus)}</span></div>
              {entry.overtime_amount > 0 && <div className="flex justify-between"><span>Overtime</span><span>{fmtINR(entry.overtime_amount)}</span></div>}
              {entry.extra_allowance > 0 && <div className="flex justify-between"><span>Extra allowance</span><span>{fmtINR(entry.extra_allowance)}</span></div>}
              {entry.lop_deduction > 0 && <div className="flex justify-between text-[hsl(var(--danger))]"><span>Loss of pay ({entry.lop_days} d)</span><span>-{fmtINR(entry.lop_deduction)}</span></div>}
              <div className="flex justify-between font-semibold border-t mt-2 pt-2"><span>Gross earnings</span><span>{fmtINR(grossEarnings)}</span></div>
            </div>
          </div>
          <div>
            <h3 className="font-display font-semibold mb-2 pb-1 border-b" style={{ borderColor: accent }}>Deductions</h3>
            <div className="space-y-1 text-sm tabular-nums">
              <div className="flex justify-between"><span>PF (employee)</span><span>{fmtINR(br.deductions.pf_employee)}</span></div>
              <div className="flex justify-between"><span>ESI (employee)</span><span>{fmtINR(br.deductions.esi_employee)}</span></div>
              <div className="flex justify-between"><span>TDS</span><span>{fmtINR(br.deductions.tds)}</span></div>
              <div className="flex justify-between"><span>Professional tax</span><span>{fmtINR(br.deductions.professional_tax)}</span></div>
              {entry.extra_deduction > 0 && <div className="flex justify-between"><span>Other deductions</span><span>{fmtINR(entry.extra_deduction)}</span></div>}
              {entry.loan_deduction > 0 && (
                <div className="flex justify-between"><span>Loan / advance EMI</span><span>{fmtINR(entry.loan_deduction)}</span></div>
              )}
              <div className="flex justify-between font-semibold border-t mt-2 pt-2"><span>Total deductions</span><span>{fmtINR(totalDeductions)}</span></div>
            </div>
          </div>
        </div>

        <div className="mt-8 rounded-lg border p-4" style={{ background: `${accent}12`, borderColor: `${accent}44` }}>
          <div className="flex justify-between items-center">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Net pay</p>
              <p className="font-display text-3xl font-semibold tabular-nums" style={{ color: accent }}>{fmtINR(finalNet)}</p>
            </div>
            {showEmpContrib && (
              <div className="text-right text-xs text-muted-foreground">
                <p>Employer contributions</p>
                <p>PF: {fmtINR(br.employer_contributions.pf_employer)}</p>
                <p>ESI: {fmtINR(br.employer_contributions.esi_employer)}</p>
              </div>
            )}
          </div>
        </div>

        <div className="mt-10 flex items-end justify-between">
          <p className="text-[10px] text-muted-foreground max-w-xl">{disclaimer}</p>
          <div className="text-right">
            <div className="border-t pt-2 min-w-[180px]" style={{ borderColor: accent }}>
              <p className="text-xs text-muted-foreground">{signature}</p>
            </div>
          </div>
        </div>
        {tpl?.footer_html && (
          <div className="mt-4 text-xs text-muted-foreground" dangerouslySetInnerHTML={{ __html: tpl.footer_html }} />
        )}
      </div>
    </div>
  );
}
